<template>
  <div class="container mx-auto px-4 py-8 max-w-3xl">
    <h1 class="text-3xl font-bold text-gray-800 mb-8">Redigera startsida</h1>

    <form @submit.prevent="submit">
      <!-- Hero Recipe Selection -->
      <Card class="mb-8">
        <CardHeader>
          <CardTitle>Hero-recept</CardTitle>
          <CardDescription>
            Välj vilket recept som visas stort överst på startsidan.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <select
            v-model="form.hero_recipe_id"
            class="w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option :value="null">Inget hero-recept</option>
            <option
              v-for="recipe in recipes"
              :key="recipe.id"
              :value="recipe.id"
            >
              {{ recipe.title }}
            </option>
          </select>

          <div class="flex flex-col gap-2 mt-4">
            <label class="text-sm font-medium"
              >Citattext (Emma tipsar-rutan)</label
            >
            <textarea
              v-model="form.hero_tip"
              rows="3"
              class="w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
              placeholder="T.ex. Använd flingsalt av hög kvalitet..."
            />
          </div>
        </CardContent>
      </Card>

      <!-- Featured Recipes Selection -->
      <Card class="mb-8">
        <CardHeader>
          <CardTitle>Utvalda recept</CardTitle>
          <CardDescription>
            Välj vilka recept som visas i kortlistan på startsidan. Du kan ordna
            dem genom att lägga till i önskad ordning.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div class="flex flex-col gap-4">
            <div
              v-for="(entry, index) in form.featured_recipe_ids"
              :key="index"
              class="flex items-center gap-3"
            >
              <span
                class="text-sm font-semibold text-muted-foreground w-6 text-center"
              >
                {{ index + 1 }}
              </span>
              <select
                v-model="form.featured_recipe_ids[index]"
                class="flex-1 rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">Välj ett recept...</option>
                <option
                  v-for="recipe in availableRecipes(index)"
                  :key="recipe.id"
                  :value="recipe.id"
                >
                  {{ recipe.title }}
                </option>
              </select>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                class="shrink-0 cursor-pointer"
                @click="moveUp(index)"
                :disabled="index === 0"
              >
                <PhArrowUp :size="18" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                class="shrink-0 cursor-pointer"
                @click="moveDown(index)"
                :disabled="index === form.featured_recipe_ids.length - 1"
              >
                <PhArrowDown :size="18" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                class="shrink-0 text-destructive cursor-pointer"
                @click="removeRecipe(index)"
              >
                <PhTrash :size="18" />
              </Button>
            </div>

            <Button
              type="button"
              variant="outline"
              class="w-full cursor-pointer"
              @click="addRecipe"
            >
              <PhPlus :size="18" class="mr-2" />
              Lägg till recept
            </Button>
          </div>
        </CardContent>
      </Card>

      <!-- Featured Categories Selection -->
      <Card class="mb-8">
        <CardHeader>
          <CardDescription>
            Välj upp till tre kategorier som visas på startsidan.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div class="flex flex-col gap-4">
            <div
              v-for="(entry, index) in form.featured_category_ids"
              :key="`category-${index}`"
              class="flex items-center gap-3"
            >
              <span
                class="text-sm font-semibold text-muted-foreground w-6 text-center"
              >
                {{ index + 1 }}
              </span>
              <select
                v-model="form.featured_category_ids[index]"
                class="flex-1 rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">Välj en kategori...</option>
                <option
                  v-for="category in availableCategories(index)"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                class="shrink-0 cursor-pointer"
                @click="moveCategoryUp(index)"
                :disabled="index === 0"
              >
                <PhArrowUp :size="18" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                class="shrink-0 cursor-pointer"
                @click="moveCategoryDown(index)"
                :disabled="index === form.featured_category_ids.length - 1"
              >
                <PhArrowDown :size="18" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                class="shrink-0 text-destructive cursor-pointer"
                @click="removeCategory(index)"
              >
                <PhTrash :size="18" />
              </Button>
            </div>

            <Button
              type="button"
              variant="outline"
              class="w-full cursor-pointer"
              @click="addCategory"
              :disabled="form.featured_category_ids.length >= 3"
            >
              <PhPlus :size="18" class="mr-2" />
              Lägg till kategori
            </Button>
          </div>
        </CardContent>
      </Card>

      <!-- About Section -->
      <Card class="mb-8">
        <CardHeader>
          <CardTitle>Om-sektion</CardTitle>
          <CardDescription>
            Två bilder och en text som visas längst ned på startsidan.
          </CardDescription>
        </CardHeader>
        <CardContent class="flex flex-col gap-6">
          <!-- Image 1 -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">Bild 1</label>
            <img
              v-if="about1Preview || about.image_1"
              :src="about1Preview || about.image_1"
              class="w-40 h-28 object-cover rounded-xl mb-2"
              alt="Bild 1"
            />
            <input
              type="file"
              accept="image/*"
              class="text-sm"
              @change="(e) => onAboutImageChange(e, 'about_image_1')"
            />
          </div>

          <!-- Image 2 -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">Bild 2</label>
            <img
              v-if="about2Preview || about.image_2"
              :src="about2Preview || about.image_2"
              class="w-40 h-28 object-cover rounded-xl mb-2"
              alt="Bild 2"
            />
            <input
              type="file"
              accept="image/*"
              class="text-sm"
              @change="(e) => onAboutImageChange(e, 'about_image_2')"
            />
          </div>

          <!-- Text -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">Text</label>
            <textarea
              v-model="form.about_text"
              rows="5"
              class="w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
              placeholder="Berätta om Emmalagat..."
            />
          </div>
        </CardContent>
      </Card>

      <!-- Submit -->
      <div class="flex justify-between items-center">
        <Link
          :href="landingPath"
          class="text-muted-foreground hover:text-foreground"
        >
          Avbryt
        </Link>
        <Button
          type="submit"
          :disabled="form.processing"
          class="cursor-pointer"
        >
          Spara ändringar
        </Button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref } from "vue";
import { Link, router, useForm } from "@inertiajs/vue3";
import { PhPlus, PhTrash, PhArrowUp, PhArrowDown } from "@phosphor-icons/vue";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import AppLayout from "@/layouts/AppLayout.vue";

const props = defineProps({
  config: { type: Object, required: true },
  recipes: { type: Array, default: () => [] },
  categories: { type: Array, default: () => [] },
  about: { type: Object, default: () => ({}) },
  currentUser: { type: Object, default: null },
  csrfToken: { type: String, default: "" },
  actionPath: { type: String, required: true },
  landingPath: { type: String, required: true },
});

const about1Preview = ref(null);
const about2Preview = ref(null);
// Plain variables (not refs) so the File objects are never wrapped in a Vue Proxy,
// which would break native FormData.append file detection.
let aboutImage1File = null;
let aboutImage2File = null;

const form = useForm({
  hero_recipe_id: props.config.hero_recipe_id ?? null,
  featured_recipe_ids:
    props.config.featured_recipe_ids.length > 0
      ? props.config.featured_recipe_ids.map((id) => id)
      : [],
  featured_category_ids:
    props.config.featured_category_ids.length > 0
      ? props.config.featured_category_ids.map((id) => id)
      : [],
  about_text: props.about.text ?? "",
  hero_tip: props.config.hero_tip ?? "",
});

function availableRecipes(currentIndex) {
  const selectedIds = form.featured_recipe_ids
    .filter((_, i) => i !== currentIndex)
    .filter((id) => id !== "");
  return props.recipes.filter((r) => !selectedIds.includes(r.id));
}

function addRecipe() {
  form.featured_recipe_ids.push("");
}

function removeRecipe(index) {
  form.featured_recipe_ids.splice(index, 1);
}

function moveUp(index) {
  if (index === 0) return;
  const items = form.featured_recipe_ids;
  [items[index - 1], items[index]] = [items[index], items[index - 1]];
}

function moveDown(index) {
  if (index === form.featured_recipe_ids.length - 1) return;
  const items = form.featured_recipe_ids;
  [items[index], items[index + 1]] = [items[index + 1], items[index]];
}

function availableCategories(currentIndex) {
  const selectedIds = form.featured_category_ids
    .filter((_, i) => i !== currentIndex)
    .filter((id) => id !== "");
  return props.categories.filter((c) => !selectedIds.includes(c.id));
}

function addCategory() {
  if (form.featured_category_ids.length >= 3) return;
  form.featured_category_ids.push("");
}

function removeCategory(index) {
  form.featured_category_ids.splice(index, 1);
}

function moveCategoryUp(index) {
  if (index === 0) return;
  const items = form.featured_category_ids;
  [items[index - 1], items[index]] = [items[index], items[index - 1]];
}

function moveCategoryDown(index) {
  if (index === form.featured_category_ids.length - 1) return;
  const items = form.featured_category_ids;
  [items[index], items[index + 1]] = [items[index + 1], items[index]];
}

function onAboutImageChange(event, fieldName) {
  const file = event.target.files?.[0] ?? null;

  if (fieldName === "about_image_1") {
    if (about1Preview.value) URL.revokeObjectURL(about1Preview.value);
    aboutImage1File = file;
    about1Preview.value = file ? URL.createObjectURL(file) : null;
  } else if (fieldName === "about_image_2") {
    if (about2Preview.value) URL.revokeObjectURL(about2Preview.value);
    aboutImage2File = file;
    about2Preview.value = file ? URL.createObjectURL(file) : null;
  }
}

onBeforeUnmount(() => {
  if (about1Preview.value) URL.revokeObjectURL(about1Preview.value);
  if (about2Preview.value) URL.revokeObjectURL(about2Preview.value);
});

function submit() {
  const data = new FormData();

  if (form.hero_recipe_id) data.append("hero_recipe_id", form.hero_recipe_id);
  data.append("hero_tip", form.hero_tip ?? "");
  data.append("about_text", form.about_text ?? "");

  form.featured_recipe_ids.forEach((id) =>
    data.append("featured_recipe_ids[]", id),
  );
  form.featured_category_ids.forEach((id) =>
    data.append("featured_category_ids[]", id),
  );

  if (aboutImage1File) data.append("about_image_1", aboutImage1File);
  if (aboutImage2File) data.append("about_image_2", aboutImage2File);

  router.patch(props.actionPath, data, { forceFormData: true });
}

defineOptions({
  layout: (h, page) =>
    h(AppLayout, { bodyMargin: "mt-20 lg:mt-50" }, () => page),
});
</script>
